import { useMemo } from "react";
import useAllCanvas from "domains/canvas/hooks/useAllCanvas";
import useDeepMemo from "domains/commons/hooks/useDeepMemo";
import { mapFileCanvasToFmFileCanvas } from "domains/file-manager/utils/mapFmFileToFile";
import { useCanvasesSearch } from "domains/search/hooks/useSearch";
import { GetAssetsApiArg } from "infra/api/generated/api";

export type UseAllCanvasAdvancedArgs = {
  query?: string;
  authorId?: string;
  collectionIds?: string[];
  sortBy?: GetAssetsApiArg["sortBy"];
  sortDirection?: GetAssetsApiArg["sortDirection"];
  dateRange?:
    | { start: string; end: string | undefined }
    | { start: undefined; end: string };
  tags?: string[];
};

export default function useAllCanvasAdvanced(
  args: UseAllCanvasAdvancedArgs | undefined
) {
  const {
    query,
    authorId,
    collectionIds,
    sortBy,
    sortDirection,
    dateRange,
    tags,
  } = args ?? {};

  const isSearch =
    !!query ||
    (!!collectionIds && collectionIds.length > 1) ||
    (!!collectionIds && collectionIds.length > 0 && !!authorId) ||
    (dateRange?.start && sortBy && sortBy !== "createdAt") ||
    (dateRange?.end && sortBy && sortBy !== "createdAt") ||
    (dateRange?.start && dateRange?.end) ||
    tags?.length;

  // ----------------------------------

  const argsApiAssets = useDeepMemo<Parameters<typeof useAllCanvas>[0]>(
    !isSearch && args
      ? {
          authorId,
          collectionId: collectionIds?.[0],
          sortBy,
          sortDirection,
          createdBefore: dateRange?.end,
          createdAfter: dateRange?.start,
        }
      : undefined
  );

  const {
    files: filesApi,
    isLoading: isLoadingApi,
    hasMore: hasMoreApi,
    loadMore: loadMoreApi,
  } = useAllCanvas(argsApiAssets);

  const fmFilesApi = useMemo(
    () => mapFileCanvasToFmFileCanvas(filesApi),
    [filesApi]
  );

  // ----------------------------------

  const sortBySearch =
    { createdAt: "createdAtTS", updatedAt: "updatedAtTS" }[sortBy ?? ""] ??
    sortBy ??
    "createdAtTS";

  const argsSearchAssets = useDeepMemo<Parameters<typeof useCanvasesSearch>[0]>(
    isSearch && args
      ? {
          query,
          isEmptyQueryAllowed: true,
          collectionIds,
          sort: `${sortBySearch}:${sortDirection}`,
          authorId,
          dateRange: dateRange && dateRange.start ? dateRange : undefined,
          tags,
        }
      : {}
  );

  const {
    files: filesSearch,
    isLoading: isLoadingSearch,
    hasMore: hasMoreSearch,
    loadMore: loadMoreSearch,
  } = useCanvasesSearch(argsSearchAssets);

  const fmFilesSearch = useMemo(
    () => mapFileCanvasToFmFileCanvas(filesSearch),
    [filesSearch]
  );

  // ----------------------------------

  const files = !isSearch ? fmFilesApi : fmFilesSearch;
  const isLoading = !isSearch ? isLoadingApi : isLoadingSearch;
  const hasMore = !isSearch ? hasMoreApi : hasMoreSearch;
  const loadMore = !isSearch ? loadMoreApi : loadMoreSearch;

  return {
    files,
    isLoading,
    hasMore,
    loadMore,
  };
}
